import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useSession } from "./SessionContext";
import { setSessionManager, setOnTokenRefreshed } from "../api/axiosConfig";
import ApiConfig from "../api/config";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ---------------------------------------------------------------
// Shared error extractor (handles RFC 7807 ProblemDetails + plain JSON)
// ---------------------------------------------------------------
const extractErrorMessage = (data, fallback) =>
  data?.detail || // ProblemDetails
  data?.message ||
  data?.error ||
  data?.title ||
  fallback;

export const AuthProvider = ({ children }) => {
  const {
    session,
    loading: sessionLoading,
    isAuthenticated,
    login: sessionLogin,
    logout: sessionLogout,
    refreshToken: sessionRefreshToken,
    sessionManager,
    checkAuth,
    getSession,
    syncFromStorage,
  } = useSession();

  const [user, setUser] = useState(null);

  // ---------------------------------------------------------------
  // Wire the session manager into axios
  // ---------------------------------------------------------------
  useEffect(() => {
    if (sessionManager) {
      setSessionManager(sessionManager);
    }
  }, [sessionManager]);

  // ---------------------------------------------------------------
  // Wire the axios → React sync callback
  // ---------------------------------------------------------------
  useEffect(() => {
    if (syncFromStorage) {
      setOnTokenRefreshed(() => {
        syncFromStorage();
      });
    }
  }, [syncFromStorage]);

  // ---------------------------------------------------------------
  // Keep local `user` in sync with the session
  // ---------------------------------------------------------------
  useEffect(() => {
    if (isAuthenticated && session) {
      setUser({ name: session.userName || "User" });
    } else {
      setUser(null);
    }
  }, [session, isAuthenticated]);

  // ---------------------------------------------------------------
  // LOGIN
  // ---------------------------------------------------------------
  const login = useCallback(
    async (username, password) => {
      const result = await sessionLogin(username, password);
      if (result.success) {
        setUser({ name: result.user.name });
      }
      return result;
    },
    [sessionLogin],
  );

  // ---------------------------------------------------------------
  // LOGOUT
  // ---------------------------------------------------------------
  const logout = useCallback(async () => {
    await sessionLogout();
    setUser(null);
  }, [sessionLogout]);

  // ---------------------------------------------------------------
  // REFRESH TOKEN
  // ---------------------------------------------------------------
  const refreshToken = useCallback(async () => {
    return await sessionRefreshToken();
  }, [sessionRefreshToken]);

  // ---------------------------------------------------------------
  // CHECK INVITATION TOKEN  →  GET /v1/SetInProgress?token=...
  // ---------------------------------------------------------------
  const checkToken = useCallback(async (token) => {
    if (!token) {
      return { success: false, message: "Missing invitation token." };
    }

    try {
      const res = await fetch(
        `${ApiConfig.ENDPOINTS.CHECKTOKEN}?token=${encodeURIComponent(token)}`,
        { method: "POST", headers: { Accept: "application/json" } },
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Example 400 response:
        // { type: "ArgumentException", title: "An error occurred",
        //   status: 400, detail: "User invite token is invalid.", ... }
        return {
          success: false,
          message: extractErrorMessage(
            data,
            "This invitation link is invalid or has expired.",
          ),
        };
      }

      return { success: true, data };
    } catch (err) {
      return {
        success: false,
        message:
          err.message ||
          "Unable to verify your invitation. Please try again later.",
      };
    }
  }, []);

  // ---------------------------------------------------------------
  // SIGNUP
  // ---------------------------------------------------------------
  const signup = useCallback(
    async (username, password, firstname, lastname, mobile, inviteToken) => {
      try {
        const role = 9999;
        const url = new URL(ApiConfig.ENDPOINTS.SIGNUP);
        url.searchParams.set("token", inviteToken);
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            username,
            password,
            firstname,
            lastname,
            mobile,
            role,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          return {
            success: false,
            message: extractErrorMessage(
              data,
              "Signup failed. Please try again.",
            ),
          };
        }

        return { success: true, data };
      } catch (err) {
        console.error("Signup error:", err);
        return {
          success: false,
          message: err.message || "Signup failed. Please try again.",
        };
      }
    },
    [],
  );

  const loading = sessionLoading;

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    refreshToken,
    checkToken, // ← now actually exposed to consumers
    checkAuth,
    getSession,
    sessionManager,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
